import { useEffect, useState, type ChangeEvent } from 'react';
import type { HexMapState } from '../../engine/render/renderer';
import { getContentRect } from '../../engine/render/modalLayout';
import {
    ACTION__SETTINGS__RESET,
    ACTION__SETTINGS__SAVE,
    ACTION__SETTINGS__TAB__CONFIG,
} from '../../engine/input/actionIds';
import {
    mergeGameConfig,
    validateGameConfig,
    type GameConfig,
    type ConfigError,
} from '../../config/gameConfig';
import { t } from '../../i18n/translations';

const SETTINGS_MODAL_ID = 'settings_modal';

type OverlayRect = { left: number; top: number; width: number; height: number };

type Props = {
    canvasRef: React.RefObject<HTMLCanvasElement | null>;
    stateRef: React.RefObject<HexMapState | null>;
};

export const SettingsOverlay = ({ canvasRef, stateRef }: Props) => {
    const [visible, setVisible] = useState(false);
    const [rect, setRect] = useState<OverlayRect | null>(null);
    const [text, setText] = useState('');
    const [error, setError] = useState<ConfigError | null>(null);

    useEffect(() => {
        let frame = 0;
        const tick = () => {
            const state = stateRef.current;
            const canvas = canvasRef.current;
            if (!state || !canvas) {
                if (visible) setVisible(false);
                frame = window.requestAnimationFrame(tick);
                return;
            }

            const modal = state.modals.find((m) => m.id === SETTINGS_MODAL_ID) || null;
            if (!modal || modal.activeTabId !== ACTION__SETTINGS__TAB__CONFIG) {
                if (visible) setVisible(false);
                frame = window.requestAnimationFrame(tick);
                return;
            }

            if (!state.settingsEditor.text) {
                state.settingsEditor.text = JSON.stringify(state.config, null, 2);
            }

            if (state.settingsEditor.text !== text) {
                setText(state.settingsEditor.text);
            }
            if (state.settingsEditor.error !== error) {
                setError(state.settingsEditor.error);
            }

            const content = getContentRect(modal.rect);
            const tabRect = modal.tabScroll.rect;
            const tabBottom = tabRect ? tabRect.y + tabRect.h + 8 : content.y;
            const top = Math.max(content.y, tabBottom);
            const height = Math.max(0, content.y + content.h - top);
            const nextRect = {
                left: content.x,
                top,
                width: content.w,
                height,
            };

            setRect(nextRect);
            if (!visible) setVisible(true);

            frame = window.requestAnimationFrame(tick);
        };

        frame = window.requestAnimationFrame(tick);
        return () => window.cancelAnimationFrame(frame);
    }, [canvasRef, stateRef, text, visible, error]);

    if (!visible || !rect) return null;
    const lang = stateRef.current?.config.ui.language ?? 'en';

    const onChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const next = e.target.value;
        setText(next);
        const state = stateRef.current;
        if (!state) return;
        state.settingsEditor.text = next;
        try {
            const parsed = JSON.parse(next) as GameConfig;
            const validationError = validateGameConfig(mergeGameConfig(state.config, parsed || {}));
            state.settingsEditor.error = validationError;
            setError(validationError);
        } catch (_err) {
            const nextError = { key: 'settings.invalidJson' } as ConfigError;
            state.settingsEditor.error = nextError;
            setError(nextError);
        }
        state.dirty = true;
    };

    const runAction = (actionId: string) => {
        const state = stateRef.current;
        if (!state) return;
        const modal = state.modals.find((m) => m.id === SETTINGS_MODAL_ID);
        if (!modal) return;
        if (modal.handleAction(actionId, state)) {
            setText(state.settingsEditor.text);
            setError(state.settingsEditor.error);
            state.dirty = true;
        }
    };

    return (
        <div
            className="hex-map__settings"
            style={{
                left: `${rect.left}px`,
                top: `${rect.top}px`,
                width: `${rect.width}px`,
                height: `${rect.height}px`,
            }}
        >
            <div className="hex-map__settings-header">
                <span className="hex-map__settings-label">{t(lang, 'settings.overlayLabel')}</span>
                <div className="hex-map__settings-actions">
                    <button type="button" onClick={() => runAction(ACTION__SETTINGS__SAVE)}>
                        {t(lang, 'settings.save')}
                    </button>
                    <button type="button" onClick={() => runAction(ACTION__SETTINGS__RESET)}>
                        {t(lang, 'settings.reset')}
                    </button>
                </div>
            </div>
            {error ? (
                <div className="hex-map__settings-error">
                    {t(lang, 'settings.error', t(lang, error.key, ...(error.args || [])))}
                </div>
            ) : null}
            <textarea
                className="hex-map__settings-textarea"
                value={text}
                onChange={onChange}
                spellCheck={false}
            />
        </div>
    );
};
